import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import { Timestamp, Timezone, TypeGuard } from '@principle-theorem/shared';
import { flow, isString } from 'lodash';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { convertExactId, getExactSourceDate } from '../../util/helpers';
import { PatientSourceEntity } from './patient';

export const PATIENT_PATHOLOGY_NOTE_RESOURCE_TYPE = 'patientPathologyNote';

export const PATIENT_PATHOLOGY_NOTE_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Patient Pathology Notes List',
    description: '',
    idPrefix: PATIENT_PATHOLOGY_NOTE_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IExactPathologyNote {
  id: string;
  patient_id: string;
  date: string;
  note: string;
}

export interface IExactPathologyNoteTranslations {
  date: Timestamp;
}

export interface IExactPathologyNoteFilters {
  patientId: string;
}

function isExactPathologyNote(
  data: unknown,
  logError: boolean
): data is IExactPathologyNote {
  return TypeGuard.interface<IExactPathologyNote>(
    {
      id: isString,
      patient_id: isString,
      date: isString,
      note: isString,
    },
    logError
  )(data);
}

const PATHOLOGY_NOTE_SOURCE_QUERY = `
SELECT
  p._record_id::TEXT AS id,
  cp.patientid::TEXT AS patient_id,
  SUBSTRING(p.storeddate::TEXT, 9, 2) || '/' || SUBSTRING(p.storeddate::TEXT, 6, 2) || '/' || SUBSTRING(p.storeddate::TEXT, 1, 4) || ' 00:00:00' AS date,
  string_agg(n.usercomment, '' ORDER BY n.position::INTEGER) AS note
FROM patholgy p
JOIN note n ON n.keyguid::TEXT = p.pathologynotesguid::TEXT
JOIN convpatient cp
  ON LTRIM(SPLIT_PART(cp.patientid, ':', 1), '0') = LTRIM(p.debtorid::TEXT, '0')
WHERE NOT p._is_deleted
  AND p.pathologynotesguid != '4294967295'
GROUP BY p._record_id, cp.patientid, p.storeddate
`;

export type ExactPathologyNote = ISourceEntityTypes<
  IExactPathologyNote,
  IExactPathologyNoteTranslations,
  IExactPathologyNoteFilters
>;

export class PatientPathologyNotesSourceEntity extends BaseSourceEntity<ExactPathologyNote> {
  sourceEntity = PATIENT_PATHOLOGY_NOTE_SOURCE_ENTITY;
  entityResourceType = PATIENT_PATHOLOGY_NOTE_RESOURCE_TYPE;
  sourceQuery = PATHOLOGY_NOTE_SOURCE_QUERY;
  verifySourceFn = isExactPathologyNote;

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([transformPathologyNoteResults]);

  getSourceRecordId(data: IExactPathologyNote): string {
    return data.id;
  }

  getSourceLabel(data: IExactPathologyNote): string {
    return `${data.patient_id}-${data.id}`;
  }

  translate(
    data: IExactPathologyNote,
    timezone: Timezone
  ): IExactPathologyNoteTranslations {
    return {
      date: getExactSourceDate(data.date, timezone),
    };
  }

  getFilterData(data: IExactPathologyNote): IExactPathologyNoteFilters {
    return {
      patientId: data.patient_id,
    };
  }
}

function transformPathologyNoteResults(
  rows: IExactPathologyNote[]
): IExactPathologyNote[] {
  return rows.map((row) => ({
    ...row,
    patient_id: convertExactId(row.patient_id),
  }));
}
